import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveCompanyWithSirenGuard, askSiren } from '../lib/companies';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { SearchableCombobox } from '../components/ui/searchable-combobox';
// DropdownMenu removed - using direct action icons instead
import { Skeleton } from '../components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import {
    Plus,
    Building2,
    User,
    Calendar as CalendarIcon,
    Euro,
    Pencil,
    Trash2,
    FileText,
    Eye,
    Loader2,
    GripVertical,
    Search,
    X,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    useSortable,
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Colors for different users
const USER_COLORS = [
    { bg: 'bg-blue-100 dark:bg-blue-900/30', border: 'border-l-blue-500' },
    { bg: 'bg-green-100 dark:bg-green-900/30', border: 'border-l-green-500' },
    { bg: 'bg-purple-100 dark:bg-purple-900/30', border: 'border-l-purple-500' },
    { bg: 'bg-orange-100 dark:bg-orange-900/30', border: 'border-l-orange-500' },
    { bg: 'bg-pink-100 dark:bg-pink-900/30', border: 'border-l-pink-500' },
    { bg: 'bg-cyan-100 dark:bg-cyan-900/30', border: 'border-l-cyan-500' },
    { bg: 'bg-yellow-100 dark:bg-yellow-900/30', border: 'border-l-yellow-500' },
    { bg: 'bg-red-100 dark:bg-red-900/30', border: 'border-l-red-500' },
];

const getUserColor = (userId, userColorMap) => {
    if (!userId || !userColorMap[userId]) return { bg: '', border: '' };
    return userColorMap[userId];
};

// Draggable Card Component
const DraggableCard = ({ opp, navigate, openEditModal, openDetailModal, handleRequestQuote, handleDelete, userColorMap }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: opp.id,
    });
    
    const userColor = getUserColor(opp.owner_user_id, userColorMap);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Card
            ref={setNodeRef}
            style={style}
            className={`border-l-4 ${userColor.border} ${userColor.bg} bg-card p-5 rounded-lg shadow-soft border border-border/40 
                       hover:-translate-y-1 hover:shadow-md hover:border-primary/30 
                       transition-all duration-200 ease-out cursor-grab active:cursor-grabbing
                       ${isDragging ? 'shadow-lg ring-2 ring-primary/50' : ''}`}
            data-testid={`opportunity-card-${opp.id}`}
        >
            <CardContent className="p-0">
                <div className="flex items-start gap-3">
                    {/* Drag Handle */}
                    <button
                        {...attributes}
                        {...listeners}
                        className="cursor-grab active:cursor-grabbing p-1.5 -ml-1 rounded-lg hover:bg-muted/50 transition-colors"
                        title="Glisser-déposer"
                    >
                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                    </button>
                    
                    <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate text-foreground">{opp.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1.5 flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5" />
                            {opp.company_name}
                        </p>
                        
                        {opp.estimated_amount && (
                            <p className="text-sm font-semibold text-green-600 mt-3">
                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(opp.estimated_amount)}
                            </p>
                        )}
                        
                        {opp.probability && (
                            <Badge variant="outline" className="mt-3 text-xs border-border/40">
                                {opp.probability}%
                            </Badge>
                        )}
                    </div>
                    
                    {/* Action Icons */}
                    <div className="flex flex-col gap-1.5">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full hover:bg-muted/80 transition-colors"
                            onClick={() => openDetailModal(opp)}
                            title="Voir les détails"
                            data-testid={`opp-view-${opp.id}`}
                        >
                            <Eye className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full hover:bg-blue-500/10 transition-colors"
                            onClick={() => openEditModal(opp)}
                            title="Modifier"
                            data-testid={`opp-edit-${opp.id}`}
                        >
                            <Pencil className="w-4 h-4 text-muted-foreground hover:text-blue-500" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full hover:bg-purple-500/10 transition-colors"
                            onClick={() => handleRequestQuote(opp.id)}
                            title="Demander un devis"
                            data-testid={`opp-quote-${opp.id}`}
                        >
                            <FileText className="w-4 h-4 text-muted-foreground hover:text-purple-500" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full hover:bg-orange-500/10 transition-colors"
                            onClick={() => handleDelete(opp.id)}
                            title="Archiver"
                            data-testid={`opp-archive-${opp.id}`}
                        >
                            <Trash2 className="w-4 h-4 text-muted-foreground hover:text-orange-500" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// Droppable Stage Component
const DroppableStage = ({ stage, opportunities, totalAmount, formatCurrency, navigate, openEditModal, openDetailModal, handleRequestQuote, handleDelete, userColorMap }) => {
    const { setNodeRef, isOver } = useSortable({
        id: stage.id,
        data: { type: 'stage' },
    });

    return (
        <div
            ref={setNodeRef}
            className={`bg-muted/30 rounded-xl p-5 min-w-[340px] flex flex-col transition-all duration-200 ${isOver ? 'ring-2 ring-primary/50 bg-primary/5' : ''}`}
            data-testid={`stage-${stage.id}`}
        >
            {/* Column Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: stage.color || '#6B7280' }}
                    />
                    <h3 className="font-semibold font-heading text-foreground">{stage.label}</h3>
                    <Badge variant="secondary" className="ml-1 bg-muted border-0">
                        {opportunities.length}
                    </Badge>
                </div>
            </div>
            
            {totalAmount > 0 && (
                <p className="text-sm text-muted-foreground mb-4 font-medium">
                    {formatCurrency(totalAmount)}
                </p>
            )}

            {/* Cards */}
            <SortableContext items={opportunities.map(o => o.id)} strategy={verticalListSortingStrategy}>
                <ScrollArea className="flex-1 max-h-[calc(100vh-300px)]">
                    <div className="space-y-4 pr-2 min-h-[100px]">
                        {opportunities.length === 0 ? (
                            <div className="text-center py-10 text-muted-foreground text-sm border-2 border-dashed border-border/40 rounded-lg bg-muted/20">
                                Déposez ici
                            </div>
                        ) : (
                            opportunities.map((opp) => (
                                <DraggableCard
                                    key={opp.id}
                                    opp={opp}
                                    navigate={navigate}
                                    openEditModal={openEditModal}
                                    openDetailModal={openDetailModal}
                                    handleRequestQuote={handleRequestQuote}
                                    handleDelete={handleDelete}
                                    userColorMap={userColorMap}
                                />
                            ))
                        )}
                    </div>
                </ScrollArea>
            </SortableContext>
        </div>
    );
};

const Pipeline = () => {
    const { api, user, isCommercial, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [stages, setStages] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showQuoteModal, setShowQuoteModal] = useState(false);
    const [showQuickCreateCompany, setShowQuickCreateCompany] = useState(false);
    const [showQuickCreateContact, setShowQuickCreateContact] = useState(false);
    const [quickCreateName, setQuickCreateName] = useState('');
    const [selectedOpportunity, setSelectedOpportunity] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [userColorMap, setUserColorMap] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    
    // Form state
    const [formData, setFormData] = useState({
        company_id: searchParams.get('company') || '',
        contact_id: '',
        title: '',
        description: '',
        pipeline_stage_id: '',
        estimated_amount: '',
        probability: '',
        expected_close_date: '',
        source: '',
    });

    useEffect(() => {
        fetchPipelineData();
    }, []);

    const fetchPipelineData = async () => {
        setLoading(true);
        try {
            const [stagesRes, oppsRes, companiesRes] = await Promise.all([
                api.get('/pipeline-stages?module=commercial'),
                api.get('/opportunities'),
                api.get('/companies'),
            ]);
            
            setStages(stagesRes.data);
            setOpportunities(oppsRes.data);
            setCompanies(companiesRes.data);
            
            // Create user color map from opportunities
            const uniqueOwners = [...new Set(oppsRes.data.map(o => o.owner_user_id).filter(Boolean))];
            const colorMap = {};
            uniqueOwners.forEach((userId, index) => {
                colorMap[userId] = USER_COLORS[index % USER_COLORS.length];
            });
            setUserColorMap(colorMap);
            
            // Set default stage for new opportunities
            if (stagesRes.data.length > 0 && !formData.pipeline_stage_id) {
                setFormData(prev => ({ ...prev, pipeline_stage_id: stagesRes.data[0].id }));
            }
        } catch (error) {
            console.error('Failed to fetch pipeline data:', error);
            toast.error('Erreur lors du chargement du pipeline');
        } finally {
            setLoading(false);
        }
    };

    const fetchContactsForCompany = async (companyId) => {
        if (!companyId) {
            setContacts([]);
            return;
        }
        try {
            const response = await api.get(`/contacts?company_id=${companyId}`);
            setContacts(response.data);
        } catch (error) {
            console.error('Failed to fetch contacts:', error);
        }
    };

    // Quick create company
    const handleQuickCreateCompany = async (name) => {
        try {
            const siret = askSiren(name);
            if (siret === null) return;
            if (!siret) {
                toast.error('SIREN (9 chiffres) ou SIRET (14 chiffres) obligatoire');
                return;
            }
            const response = await saveCompanyWithSirenGuard(
                (force) => api.post(`/companies${force ? '?force=true' : ''}`, { legal_name: name, siret, statuses: ['prospect'] }),
                { api, canForce: isSuperAdmin() }
            );
            toast.success(`Entreprise "${name}" créée`);
            // Refresh companies and select the new one
            const companiesRes = await api.get('/companies');
            setCompanies(companiesRes.data);
            setFormData(prev => ({ ...prev, company_id: response.data.id, contact_id: '' }));
            setContacts([]);
        } catch (error) {
            console.error('Failed to create company:', error);
            if (!error.handled) toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'entreprise');
        }
    };

    // Quick create contact
    const handleQuickCreateContact = async (name) => {
        if (!formData.company_id) {
            toast.error('Veuillez d\'abord sélectionner une entreprise');
            return;
        }
        const nameParts = name.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        try {
            const response = await api.post('/contacts', {
                company_id: formData.company_id,
                first_name: firstName,
                last_name: lastName || firstName, // Use first name as last if not provided
            });
            toast.success(`Contact "${name}" créé`);
            // Refresh contacts and select the new one
            await fetchContactsForCompany(formData.company_id);
            setFormData(prev => ({ ...prev, contact_id: response.data.id }));
        } catch (error) {
            console.error('Failed to create contact:', error);
            toast.error('Erreur lors de la création du contact');
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            const data = {
                ...formData,
                estimated_amount: formData.estimated_amount ? parseFloat(formData.estimated_amount) : null,
                probability: formData.probability ? parseInt(formData.probability) : null,
            };
            
            await api.post('/opportunities', data);
            toast.success('Opportunité créée avec succès');
            setShowCreateModal(false);
            resetForm();
            fetchPipelineData();
        } catch (error) {
            console.error('Failed to create opportunity:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            const data = {
                ...formData,
                estimated_amount: formData.estimated_amount ? parseFloat(formData.estimated_amount) : null,
                probability: formData.probability ? parseInt(formData.probability) : null,
            };
            
            await api.patch(`/opportunities/${selectedOpportunity.id}`, data);
            toast.success('Opportunité mise à jour');
            setShowEditModal(false);
            fetchPipelineData();
        } catch (error) {
            console.error('Failed to update opportunity:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSubmitting(false);
        }
    };

    const handleStageChange = async (opportunityId, newStageId) => {
        try {
            await api.patch(`/opportunities/${opportunityId}`, { pipeline_stage_id: newStageId });
            toast.success('Étape mise à jour');
            fetchPipelineData();
        } catch (error) {
            console.error('Failed to update stage:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleDelete = async (opportunityId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir archiver cette opportunité ?')) return;
        
        try {
            await api.delete(`/opportunities/${opportunityId}`);
            toast.success('Opportunité archivée');
            fetchPipelineData();
        } catch (error) {
            console.error('Failed to archive opportunity:', error);
            toast.error("Erreur lors de l'archivage");
        }
    };

    const handleRequestQuote = async (opportunityId) => {
        setSelectedOpportunity(opportunities.find(o => o.id === opportunityId));
        setShowQuoteModal(true);
    };

    const resetForm = () => {
        setFormData({
            company_id: '',
            contact_id: '',
            title: '',
            description: '',
            pipeline_stage_id: stages[0]?.id || '',
            estimated_amount: '',
            probability: '',
            expected_close_date: '',
            source: '',
        });
        setContacts([]);
    };

    const openEditModal = (opp) => {
        setSelectedOpportunity(opp);
        setFormData({
            company_id: opp.company_id,
            contact_id: opp.contact_id || '',
            title: opp.title,
            description: opp.description || '',
            pipeline_stage_id: opp.pipeline_stage_id,
            estimated_amount: opp.estimated_amount?.toString() || '',
            probability: opp.probability?.toString() || '',
            expected_close_date: opp.expected_close_date || '',
            source: opp.source || '',
        });
        fetchContactsForCompany(opp.company_id);
        setShowEditModal(true);
    };

    const openDetailModal = (opp) => {
        setSelectedOpportunity(opp);
        setShowDetailModal(true);
    };

    // Filter opportunities by search query
    const filteredOpportunities = opportunities.filter(opp => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            opp.title?.toLowerCase().includes(query) ||
            opp.company_name?.toLowerCase().includes(query) ||
            opp.contact_name?.toLowerCase().includes(query) ||
            opp.owner_name?.toLowerCase().includes(query) ||
            opp.description?.toLowerCase().includes(query)
        );
    });

    const getOpportunitiesByStage = (stageId) => {
        return filteredOpportunities.filter(opp => opp.pipeline_stage_id === stageId);
    };

    const formatCurrency = (amount) => {
        if (!amount) return null;
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
    };

    // Drag & Drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor)
    );

    const [activeId, setActiveId] = useState(null);

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);
        
        if (!over) return;
        
        const opportunityId = active.id;
        const targetStageId = over.id;
        
        // Find current opportunity
        const opportunity = opportunities.find(o => o.id === opportunityId);
        if (!opportunity) return;
        
        // If dropped on same stage, do nothing
        if (opportunity.pipeline_stage_id === targetStageId) return;
        
        // Check if dropped on a stage (not another opportunity)
        const isStage = stages.some(s => s.id === targetStageId);
        if (!isStage) return;
        
        // Update stage
        await handleStageChange(opportunityId, targetStageId);
    };

    const activeOpportunity = activeId ? opportunities.find(o => o.id === activeId) : null;

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <div className="flex gap-4 overflow-hidden">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton key={i} className="h-[600px] w-[300px] flex-shrink-0" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading text-foreground">Pipeline commercial</h1>
                    <p className="text-muted-foreground mt-1">
                        {filteredOpportunities.length} opportunité{filteredOpportunities.length > 1 ? 's' : ''} 
                        {searchQuery && ` (sur ${opportunities.length})`}
                    </p>
                </div>
                <Button
                    onClick={() => {
                        resetForm();
                        setShowCreateModal(true);
                    }}
                    className="rounded-full"
                    data-testid="create-opportunity-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle opportunité
                </Button>
            </div>

            {/* Search Bar */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Rechercher par titre, entreprise, contact, commercial..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 rounded-lg border-border/40 focus:border-primary/50"
                            data-testid="pipeline-search-input"
                        />
                        {searchQuery && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full"
                                onClick={() => setSearchQuery('')}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Kanban Board */}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <ScrollArea className="w-full">
                    <div className="flex gap-4 pb-4 min-w-max">
                        {stages.map((stage) => {
                            const stageOpps = getOpportunitiesByStage(stage.id);
                            const totalAmount = stageOpps.reduce((sum, opp) => sum + (opp.estimated_amount || 0), 0);
                            
                            return (
                                <DroppableStage
                                    key={stage.id}
                                    stage={stage}
                                    opportunities={stageOpps}
                                    totalAmount={totalAmount}
                                    formatCurrency={formatCurrency}
                                    navigate={navigate}
                                    openEditModal={openEditModal}
                                    openDetailModal={openDetailModal}
                                    handleRequestQuote={handleRequestQuote}
                                    handleDelete={handleDelete}
                                    userColorMap={userColorMap}
                                />
                            );
                        })}
                    </div>
                    <ScrollBar orientation="horizontal" />
                </ScrollArea>
                
                <DragOverlay>
                    {activeOpportunity ? (
                        <Card className="kanban-card shadow-xl opacity-90 cursor-grabbing">
                            <CardContent className="p-4">
                                <h4 className="font-medium">{activeOpportunity.title}</h4>
                                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />
                                    {activeOpportunity.company_name}
                                </p>
                            </CardContent>
                        </Card>
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
                if (!open) {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                }
            }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {showEditModal ? 'Modifier l\'opportunité' : 'Nouvelle opportunité'}
                        </DialogTitle>
                        <DialogDescription>
                            {showEditModal ? 'Mettez à jour les informations' : 'Créez une nouvelle affaire commerciale'}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={showEditModal ? handleUpdate : handleCreate} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Entreprise *</Label>
                            <SearchableCombobox
                                value={formData.company_id}
                                onValueChange={(v) => {
                                    setFormData({ ...formData, company_id: v, contact_id: '' });
                                    fetchContactsForCompany(v);
                                }}
                                options={companies.map(c => ({
                                    id: c.id,
                                    label: c.legal_name,
                                    sublabel: c.city || c.sector,
                                }))}
                                placeholder="Rechercher une entreprise..."
                                searchPlaceholder="Tapez pour rechercher..."
                                emptyMessage="Aucune entreprise trouvée"
                                createLabel="Créer l'entreprise"
                                onCreate={handleQuickCreateCompany}
                                type="company"
                                data-testid="opp-company-select"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Contact</Label>
                            <SearchableCombobox
                                value={formData.contact_id}
                                onValueChange={(v) => setFormData({ ...formData, contact_id: v })}
                                options={contacts.map(c => ({
                                    id: c.id,
                                    label: `${c.first_name} ${c.last_name}`,
                                    sublabel: c.job_title,
                                }))}
                                placeholder={formData.company_id ? "Rechercher un contact..." : "Sélectionnez d'abord une entreprise"}
                                searchPlaceholder="Tapez pour rechercher..."
                                emptyMessage="Aucun contact trouvé"
                                createLabel="Créer le contact"
                                onCreate={formData.company_id ? handleQuickCreateContact : undefined}
                                type="contact"
                                disabled={!formData.company_id}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Titre *</Label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Ex: Campagne publicité Q1"
                                required
                                className="rounded-lg"
                                data-testid="opp-title-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Étape</Label>
                            <Select
                                value={formData.pipeline_stage_id}
                                onValueChange={(v) => setFormData({ ...formData, pipeline_stage_id: v })}
                            >
                                <SelectTrigger className="rounded-lg">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {stages.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-2 h-2 rounded-full"
                                                    style={{ backgroundColor: s.color || '#6B7280' }}
                                                />
                                                {s.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Montant estimé (€)</Label>
                                <Input
                                    type="number"
                                    value={formData.estimated_amount}
                                    onChange={(e) => setFormData({ ...formData, estimated_amount: e.target.value })}
                                    placeholder="10000"
                                    className="rounded-lg"
                                    data-testid="opp-amount-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Probabilité (%)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={formData.probability}
                                    onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
                                    placeholder="50"
                                    className="rounded-lg"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Date de clôture estimée</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={`w-full justify-start text-left font-normal rounded-lg hover:bg-muted transition-colors ${!formData.expected_close_date && 'text-muted-foreground'}`}
                                        data-testid="date-picker-trigger"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {formData.expected_close_date && formData.expected_close_date !== '' ? (
                                            format(parseISO(formData.expected_close_date), 'dd MMMM yyyy', { locale: fr })
                                        ) : (
                                            <span>Sélectionner une date</span>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={formData.expected_close_date && formData.expected_close_date !== '' ? parseISO(formData.expected_close_date) : undefined}
                                        onSelect={(date) => setFormData({ ...formData, expected_close_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                                        locale={fr}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={3}
                                className="rounded-lg"
                            />
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setShowEditModal(false);
                                    resetForm();
                                }}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="opp-submit-btn">
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {showEditModal ? 'Mise à jour...' : 'Création...'}
                                    </>
                                ) : (
                                    showEditModal ? 'Mettre à jour' : 'Créer'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Quote Request Modal */}
            <QuoteRequestModal
                open={showQuoteModal}
                onOpenChange={setShowQuoteModal}
                opportunity={selectedOpportunity}
                onSuccess={() => {
                    setShowQuoteModal(false);
                    toast.success('Demande de devis créée');
                }}
            />

            {/* Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {selectedOpportunity?.title}
                        </DialogTitle>
                        <DialogDescription>
                            Détails de l'opportunité
                        </DialogDescription>
                    </DialogHeader>

                    {selectedOpportunity && (
                        <div className="space-y-6">
                            {/* Company & Contact */}
                            <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Building2 className="w-5 h-5 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-foreground">{selectedOpportunity.company_name}</p>
                                    {selectedOpportunity.contact_name && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Contact : {selectedOpportunity.contact_name}
                                        </p>
                                    )}
                                    <Button 
                                        variant="link" 
                                        className="p-0 h-auto text-sm text-primary mt-2"
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            navigate(`/companies/${selectedOpportunity.company_id}`);
                                        }}
                                    >
                                        Voir l'entreprise →
                                    </Button>
                                </div>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Montant estimé</p>
                                    <p className="text-lg font-semibold text-green-600">
                                        {selectedOpportunity.estimated_amount 
                                            ? formatCurrency(selectedOpportunity.estimated_amount)
                                            : '-'
                                        }
                                    </p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Probabilité</p>
                                    <p className="text-lg font-semibold text-foreground">
                                        {selectedOpportunity.probability ? `${selectedOpportunity.probability}%` : '-'}
                                    </p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Étape</p>
                                    <Badge 
                                        style={{ backgroundColor: selectedOpportunity.stage_color || '#6B7280' }}
                                        className="text-white"
                                    >
                                        {selectedOpportunity.stage_label}
                                    </Badge>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Date de clôture prévue</p>
                                    <p className="font-medium text-foreground">
                                        {selectedOpportunity.expected_close_date 
                                            ? format(parseISO(selectedOpportunity.expected_close_date), 'dd MMM yyyy', { locale: fr })
                                            : '-'
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Source */}
                            {selectedOpportunity.source && (
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Source</p>
                                    <p className="font-medium text-foreground">{selectedOpportunity.source}</p>
                                </div>
                            )}

                            {/* Description */}
                            {selectedOpportunity.description && (
                                <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground">Description</p>
                                    <p className="text-sm text-foreground whitespace-pre-wrap bg-muted/50 p-3 rounded-lg">
                                        {selectedOpportunity.description}
                                    </p>
                                </div>
                            )}

                            {/* Owner */}
                            {selectedOpportunity.owner_name && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <User className="w-4 h-4" />
                                    Assigné à : {selectedOpportunity.owner_name}
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setShowDetailModal(false)}
                            className="rounded-full"
                        >
                            Fermer
                        </Button>
                        <Button
                            onClick={() => {
                                setShowDetailModal(false);
                                openEditModal(selectedOpportunity);
                            }}
                            className="rounded-full"
                        >
                            <Pencil className="w-4 h-4 mr-2" />
                            Modifier
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Quote Request Modal Component
const QuoteRequestModal = ({ open, onOpenChange, opportunity, onSuccess }) => {
    const { api } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        support: '',
        estimated_amount: '',
        desired_date: '',
        urgency: 'normal',
        notes: '',
    });

    useEffect(() => {
        if (opportunity) {
            setFormData(prev => ({
                ...prev,
                title: `Devis - ${opportunity.title}`,
                estimated_amount: opportunity.estimated_amount?.toString() || '',
            }));
        }
    }, [opportunity]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            await api.post('/quote-requests', {
                opportunity_id: opportunity.id,
                ...formData,
                estimated_amount: formData.estimated_amount ? parseFloat(formData.estimated_amount) : null,
            });
            onSuccess();
        } catch (error) {
            console.error('Failed to create quote request:', error);
            toast.error('Erreur lors de la création de la demande');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading">Demander un devis</DialogTitle>
                    <DialogDescription>
                        Créez une demande de devis pour {opportunity?.company_name}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label>Objet *</Label>
                        <Input
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                            className="rounded-lg"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Description *</Label>
                        <Textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            placeholder="Détails de la prestation..."
                            className="rounded-lg"
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Support / Magazine *</Label>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { value: 'decideur', label: 'Décideur' },
                                { value: 'place_to_b', label: 'The Place to B' },
                                { value: 'mag_immo', label: "Le Mag' de l'Immo" },
                                { value: 'jaime_dijon', label: "J'aime Dijon" },
                                { value: 'jaime_beaune', label: "J'aime Beaune" },
                                { value: 'yafa', label: 'Yafa' },
                                { value: 'autre', label: 'Autre' },
                            ].map((item) => (
                                <div key={item.value} className="flex items-center space-x-2">
                                    <Checkbox
                                        id={`support-${item.value}`}
                                        checked={formData.support?.includes(item.value)}
                                        onCheckedChange={(checked) => {
                                            const currentSupports = formData.support ? formData.support.split(',').filter(Boolean) : [];
                                            if (checked) {
                                                setFormData({ ...formData, support: [...currentSupports, item.value].join(',') });
                                            } else {
                                                setFormData({ ...formData, support: currentSupports.filter(s => s !== item.value).join(',') });
                                            }
                                        }}
                                    />
                                    <Label htmlFor={`support-${item.value}`} className="text-sm cursor-pointer">{item.label}</Label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Urgence</Label>
                            <Select
                                value={formData.urgency}
                                onValueChange={(v) => setFormData({ ...formData, urgency: v })}
                            >
                                <SelectTrigger className="rounded-lg hover:bg-muted transition-colors">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Basse</SelectItem>
                                    <SelectItem value="normal">Normale</SelectItem>
                                    <SelectItem value="high">Haute</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Montant total (€ HT) *</Label>
                            <Input
                                type="number"
                                value={formData.estimated_amount}
                                onChange={(e) => setFormData({ ...formData, estimated_amount: e.target.value })}
                                className="rounded-lg"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Date souhaitée</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={`w-full justify-start text-left font-normal rounded-lg hover:bg-muted transition-colors ${!formData.desired_date && 'text-muted-foreground'}`}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {formData.desired_date ? (
                                        format(parseISO(formData.desired_date), 'dd MMM yyyy', { locale: fr })
                                    ) : (
                                        <span>Date</span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={formData.desired_date ? parseISO(formData.desired_date) : undefined}
                                    onSelect={(date) => setFormData({ ...formData, desired_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                                    locale={fr}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="rounded-full"
                        >
                            Annuler
                        </Button>
                        <Button type="submit" disabled={submitting} className="rounded-full">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer la demande'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default Pipeline;
